<template>
  <!-- Finestra flotant -->
  <div
    v-if="visible"
    class="draggable-window shadow-4 bg-white"
    :style="{ top: `${position.y}px`, left: `${position.x}px`, zIndex: zIndex }"
    @mousedown.self="bringToFront"
    ref="window"
  >
    <div
      class="window-header bg-primary text-white q-pa-sm cursor-move"
      style="min-width: 230px"
      @mousedown="startDrag"
    >
      <div>
        {{ $t('tools.panorama.drawingNavigationPanel') }}
      </div>
      <q-space/>
      <q-btn dense flat round :icon="minimize ? 'keyboard_arrow_down' : 'keyboard_arrow_up'" @click="minimize = !minimize">
        <q-tooltip>{{ minimize ? $t('actions.maximize') : $t('actions.minimize') }}</q-tooltip>
      </q-btn>
      <q-btn dense flat round icon="close" @click="visible = false">
        <q-tooltip>{{ $t('actions.close') | capitalize }}</q-tooltip>
      </q-btn>
    </div>

    <div class="q-pa-md row justify-center" v-show="!minimize" style="width: 500px;">
      <q-btn outline v-if="measuring" @click="stopMapMeasuring">
        {{ $t('tools.draw.stop') }}
      </q-btn>
      <q-btn outline icon="timeline" v-else-if="!measuring && !multiPointLayer" :loading="fetchingPlaylist" @click="startMeasuring">
        &nbsp;{{ $t('tools.panorama.generateNewRoute') }}
      </q-btn>
      <q-btn outline v-else-if="!measuring && multiPointLayer" @click="playlistLayerRemove()">
        {{ $t('tools.panorama.removePlaylist') }}
      </q-btn>

      <div v-if="measuring" class="text-grey-7 q-pt-sm">
        {{ $t('tools.panorama.drawingNavigationPanelInfo') }}
      </div>
      <div v-else-if="playlist.length === 0" class="text-grey-7 q-pt-sm">
        {{ $t('tools.panorama.drawingNavigationPanelInfo2') }}
      </div>
      <div v-else class="text-grey-7 q-pt-sm">
        {{ $t('tools.panorama.drawingNavigationPanelInfo3') }}
      </div>

      <q-card flat v-if="playlistCount > 0">
        <q-card-section class="text-subtitle text-weight-bold q-pb-xs">
          <div class="row">
            {{ $t('tools.panorama.playlist') }}
            <q-space />
            <q-btn icon="sync" @click="reversePlaylist" flat dense round size="sm">
              <q-tooltip>{{ $t('tools.panorama.reversePlaylist') }}</q-tooltip>
            </q-btn>
          </div>
        </q-card-section>
        <q-card-section class="text-subtitle2 q-pt-xs">
          <q-list style="max-height: 250px; overflow-y: auto" separator bordered>
            <q-item
              v-for="(item, index) in playlist" :key="index"
              clickable @click="goToFromPlaylist(item)"
              :class="index === currentPlaylistIndex ? 'bg-primary text-white' : 'text-grey-7'"
            >
              <q-item-section>
                <q-item-label>
                  {{ index + 1 }}. {{ item.latLng.lat.toFixed(6) }}, {{ item.latLng.lng.toFixed(6) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-section class="row items-center no-wrap items-end q-gutter-md">
          <div class="text-center">
            <q-btn flat round icon="fast_rewind"  @click="previous">
              <q-tooltip>{{ $t('names.previous') }}</q-tooltip>
            </q-btn>
            <q-btn v-if="playing" flat round icon="pause" @click="stopPlaying">
              <q-tooltip>{{ $t('tools.panorama.stopPlaying') }}</q-tooltip>
            </q-btn>
            <q-btn v-else flat round icon="play_arrow" @click="play">
              <q-tooltip>{{ $t('tools.panorama.playPlaylist') }}</q-tooltip>
            </q-btn>
            <q-btn flat round icon="fast_forward" @click="next">
              <q-tooltip>{{ $t('names.next') }}</q-tooltip>
            </q-btn>
          </div>

          <q-space />

          <div class="text-right">
            <div class="row items-rigth no-wrap q-gutter-md">
              <q-input
                :hint="$t('tools.panorama.timePerPoint')"
                v-model="time"
                type="number"
                min="1"
                max="10"
                step="1"
                style="width: 130px;"
              />
              <q-input
                :hint="$t('tools.panorama.stepBetweenPoints')"
                v-model="steps"
                type="number"
                min="1"
                max="100"
                step="1"
                style="width: 130px;"
              />
            </div>
          </div>
        </q-card-section>
        <q-linear-progress :value="navProgress" color="pink" />
        <div class="text-right">Punt: {{ currentPlaylistIndex + 1 }} / {{ playlistCount }}</div>
      </q-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { QBtn, QCard, QCardSection, QInput, QLinearProgress, QList, QItem, QItemLabel, QItemSection, QSpace, QTooltip } from 'quasar'
import { api, lib } from '@giscube/geoportal'
const geoportalApi = api.geoportal
const createLayer = lib.geomUtils.createLayer
const L = lib.L

export default {
  components: {
    QBtn, QCard, QCardSection, QInput, QLinearProgress, QList, QItem, QItemLabel, QItemSection, QSpace, QTooltip
  },
  data () {
    return {
      measuring: false,
      playlist: [],
      dragging: false,
      position: { x: 100, y: 100 },
      offset: { x: 0, y: 0 },
      zIndex: 4000,
      currentPlaylistIndex: 0,
      fetchingPlaylist: false,
      multiPointLayer: null,
      playing: false,
      time: 3,
      steps: 1,
      minimize: false
    }
  },
  computed: {
    playlistCount () {
      return this.playlist.length
    },
    navProgress () {
      if (this.playlistCount === 0) return 0
      return (this.currentPlaylistIndex + 1) / this.playlistCount
    },
    visible: {
      get () {
        return this.$store.state.panorama.showDrawingNavigationWindow
      },
      set (value) {
        this.$store.commit('panorama/setShowDrawingNavigationWindow', value)
      }
    },
    map () {
      return this.$store.state.map.mapObject
    },
    measureLayer () {
      if (this.map && this.map.measureControl?.measures?.length > 0) {
        return this.map.measureControl?.measures[0].layer
      } else {
        return false
      }
    },
    linestring () {
      return typeof this.measureLayer?.getLayers === 'function' && this.measureLayer?.getLayers()[0]._latlngs.map(latLng => `${latLng.lat},${latLng.lng}`).join('|')
    }
  },
  methods: {
    startDrag (e) {
      this.dragging = true
      this.offset.x = e.clientX - this.position.x
      this.offset.y = e.clientY - this.position.y

      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    onDrag (e) {
      if (!this.dragging) return

      const windowEl = this.$refs.window
      const windowWidth = windowEl.offsetWidth
      const windowHeight = windowEl.offsetHeight

      const maxX = window.innerWidth - windowWidth
      const maxY = window.innerHeight - windowHeight

      let newX = e.clientX - this.offset.x
      let newY = e.clientY - this.offset.y

      // Limita dins la finestra visible
      newX = Math.max(0, Math.min(newX, maxX))
      newY = Math.max(0, Math.min(newY, maxY))

      this.position.x = newX
      this.position.y = newY
    },
    stopDrag () {
      this.dragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },
    bringToFront () {
      this.zIndex += 1 // opcional, per si tens múltiples finestres
    },
    updateSharedLayers () {
      this.$nextTick(() => {
        this.$store.dispatch('map/updateSharedLayers')
      })
    },
    finishedpath () {
      this.stopMapMeasuring()
    },
    startMeasuring () {
      this.$emit('measuring')
      const type = 'linestring'
      if (!this.map) {
        this.$except('Measure control is missing the map property')
        return
      }
      if (!this.map.measureControl) {
        this.$except('Measure control is missing the map measureControl')
        return
      }
      this.measuring = true
      this.$store.commit('setCurrentTool', this.map.measureControl)
      this.map.on('measure:measurestop', this.stopMeasuring)
      this.map.on('measure:finishedpath', this.finishedpath)
      geoportalApi.newToolRegister(this.$config.registers, type, this.$store.getters['auth/config'], this.$store.state.auth.username)
      this.$nextTick(_ => {
        this.map.measureControl.startMeasuring({ measureArea: false })
      })
    },
    addMeasure (type) {
      this.measuring = true
      this.$store.commit('setCurrentTool', this.map.measureControl)
      createLayer({
        map: this.map,
        type
      })
        .then(measure => {
          this.finishAddMeasure(measure, type)
          geoportalApi.newToolRegister(this.$config.registers, type, this.$store.getters['auth/config'], this.$store.state.auth.username)
        })
        .catch(_ => this.stopMeasuring())
    },
    finishAddMeasure () {
      this.stopMeasuring()
    },
    stopMapMeasuring () {
      this.$store.dispatch('map/stopDrawing')
      this.$nextTick(_ => {
        // $nextTick needed to prevent infinite event loop
        this.map.measureControl.stopMeasuring()
      })
    },
    stopMeasuring () {
      this.$emit('stopmeasuring')
      if (!this.measuring) {
        return
      }
      if (this.linestring && this.linestring.length > 0) {
        this.createPlaylist()
      }
      this.measuring = false
      this.map.off('measure:measurestop', this.stopMeasuring)
      this.map.off('measure:finishedpath', this.finishedpath)
      // FIXME: hardcoded 300ms value from QueryOnClick, get from config
      setTimeout(() => {
        this.$store.commit('setCurrentTool', null)
      }, 300)
    },
    removeMeasure () {
      if (this.measureLayer) {
        this.measureLayer.remove()
      }
    },
    reversePlaylist () {
      if (this.playlist.length === 0) return
      this.playlist = this.playlist.reverse()
      this.currentPlaylistIndex = this.playlistCount - this.currentPlaylistIndex - 1
    },
    createPlaylist () {
      this.fetchingPlaylist = true
      const navigationRouteAPI = this.$config.panoramas.navigationRoute
      const config = {
        headers: this.$store.getters['auth/headers'],
        params: {
          line: this.linestring
        }
      }
      axios.get(navigationRouteAPI, config)
        .then(response => {
          this.playlist = response.data.poisLatLng
          this.addPlaylistToMap()
          this.removeMeasure()
        })
        .catch(_ => {
          this.playlist = []
          console.error('Error fetching playlist from API')
        })
        .then(_ => {
          this.fetchingPlaylist = false
        })
    },
    addPlaylistToMap () {
      if (!this.map || !this.playlist || this.playlist.length === 0) return

      // Convertir playlist a GeoJSON MultiPoint
      const geojson = {
        type: 'Feature',
        geometry: {
          type: 'MultiPoint',
          coordinates: this.playlist.map(item => [item.latLng.lng, item.latLng.lat])
        }
      }

      // Crear la capa y añadirla al mapa
      if (this.multiPointLayer) {
        this.map.removeLayer(this.multiPointLayer)
      }
      this.multiPointLayer = L.geoJSON(geojson, {
        pointToLayer: (feature, latlng) => {
          const circle = L.circle(latlng, {
            radius: 1,
            color: 'blue',
            fillColor: 'blue',
            fillOpacity: 0.5
          })
          return circle
        }
      }).addTo(this.map)
    },
    playlistLayerRemove () {
      if (this.multiPointLayer) {
        this.map.removeLayer(this.multiPointLayer)
        this.multiPointLayer = null
      }
      this.playlist = []
      this.currentPlaylistIndex = 0
    },
    async play () {
      this.playing = true
      for (let i = this.currentPlaylistIndex; i < this.playlistCount; i += parseInt(this.steps)) {
        if (this.playing === false) {
          return
        }
        this.goTo(this.playlist[i])
        this.currentPlaylistIndex = i
        await this.delay(this.time * 1000)
      }
      this.playing = false
      this.currentPlaylistIndex = 0
    },
    delay (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    stopPlaying () {
      this.playing = false
    },
    goTo (item) {
      if (this.map) {
        const head = this.$store.state.panorama.head
        this.$store.dispatch('panorama/getPanoByPoint', item.latLng)
        this.$store.commit('panorama/setHead', { head, from: 'pannellum' })
      }
    },
    goToFromPlaylist (item) {
      this.goTo(item)
      this.currentPlaylistIndex = this.playlist.indexOf(item)
      this.playing = false
    },
    previous () {
      this.currentPlaylistIndex -= 1
      if (this.currentPlaylistIndex < 0) {
        this.currentPlaylistIndex = 0
      }
      this.goTo(this.playlist[this.currentPlaylistIndex])
    },
    next () {
      this.currentPlaylistIndex += 1
      if (this.currentPlaylistIndex >= this.playlistCount) {
        this.currentPlaylistIndex = this.playlistCount - 1
      }
      this.goTo(this.playlist[this.currentPlaylistIndex])
    }
  }
}
</script>

<style scoped>
.draggable-window {
  position: fixed;
  border-radius: 8px;
}
.window-header {
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
